import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import {
  supportRepository,
  type CreateTicketInput,
  type QueueFilter,
} from '../api/support.repository';
import type { SupportTicketPriority, SupportTicketStatus } from '../model/support-ticket';

export const supportKeys = {
  all: ['support'] as const,
  mine: (userId: string) => [...supportKeys.all, 'mine', userId] as const,
  queue: (filter: QueueFilter) => [...supportKeys.all, 'queue', filter] as const,
  ticket: (id: string) => [...supportKeys.all, 'ticket', id] as const,
  messages: (ticketId: string) => [...supportKeys.all, 'messages', ticketId] as const,
  byApplication: (applicationId: string) =>
    [...supportKeys.all, 'by-application', applicationId] as const,
  admins: () => [...supportKeys.all, 'admins'] as const,
};

export function useMyTickets() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: supportKeys.mine(userId ?? 'anon'),
    queryFn: () => unwrap(supportRepository.listMine(userId as string)),
    enabled: Boolean(userId),
  });
}

/**
 * Admin ticket queue, paginated with `useInfiniteQuery` ("load more"). The
 * query key is the filter object (search included), so changing any filter
 * — including the debounced search text — starts over at page 0.
 */
export function useTicketQueue(filter: QueueFilter = {}, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: supportKeys.queue(filter),
    queryFn: ({ pageParam }) => unwrap(supportRepository.listQueue(filter, pageParam, pageSize)),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < pageSize ? undefined : allPages.length,
  });
}

export function useAdmins() {
  return useQuery({
    queryKey: supportKeys.admins(),
    queryFn: () => unwrap(supportRepository.listAdmins()),
  });
}

export function useTicket(id: string | undefined) {
  return useQuery({
    queryKey: supportKeys.ticket(id ?? 'none'),
    queryFn: () => unwrap(supportRepository.get(id as string)),
    enabled: Boolean(id),
  });
}

/** Live message thread — subscribes to Realtime inserts like chat. */
export function useTicketMessages(ticketId: string | undefined) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: supportKeys.messages(ticketId ?? 'none'),
    queryFn: () => unwrap(supportRepository.listMessages(ticketId as string)),
    enabled: Boolean(ticketId),
  });

  useEffect(() => {
    if (!ticketId) return;
    const unsubscribe = supportRepository.subscribeMessages(ticketId, () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
    });
    return unsubscribe;
  }, [ticketId, queryClient]);

  return query;
}

export function useTicketByApplication(applicationId: string | undefined) {
  return useQuery({
    queryKey: supportKeys.byApplication(applicationId ?? 'none'),
    queryFn: () => unwrap(supportRepository.findByApplication(applicationId as string)),
    enabled: Boolean(applicationId),
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: (input: CreateTicketInput) => unwrap(supportRepository.create(input)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.mine(userId ?? 'anon') });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useReplyTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => unwrap(supportRepository.reply(ticketId, body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
      void queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useAssignTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (adminId: string | null) => unwrap(supportRepository.assign(ticketId, adminId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useUpdateTicketStatus(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { status: SupportTicketStatus; note: string | null }) =>
      unwrap(supportRepository.updateStatus(ticketId, vars.status, vars.note)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useSetPriority(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (priority: SupportTicketPriority) =>
      unwrap(supportRepository.setPriority(ticketId, priority)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useReopenTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(supportRepository.reopen(ticketId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.ticket(ticketId) });
      void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}

export function useAddInternalNote(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => unwrap(supportRepository.addInternalNote(ticketId, body)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: supportKeys.messages(ticketId) });
    },
  });
}

export function useBulkUpdateStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { ids: readonly string[]; status: SupportTicketStatus }) =>
      unwrap(supportRepository.bulkUpdateStatus(vars.ids, vars.status)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, 'queue'] });
    },
  });
}
